import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

export enum CacheType {
    Session,
    Local
}
export class CacheItem {
    key: string;
    value: any;
    expiresTTL: number = -1;
    cacheType: CacheType;
    insertedAt: number;

    /**
     * Creates an instance of CacheItem.
     * @param {string} key
     * @param {*} value
     * @param {CacheType} cacheType  [Session|Local]
     * @param {number} [expiresTTL=-1] value in seconds. -1 is no expiration
     * @memberof CacheItem
     */
    constructor(
        key: string,
        value: any,
        cacheType: CacheType,
        expiresTTL: number = -1
    ) {
        this.key = key;
        this.value = value;
        this.cacheType = cacheType;
        this.expiresTTL = expiresTTL;
    }
}
@Injectable()
export class CPStorageService {
    parseJson(data) {
        return data ? JSON.parse(data) : {};
    }

    set(cacheItem: CacheItem) {
        if (!cacheItem) {
            return;
        }
        cacheItem.insertedAt = Date.now();
        if (cacheItem.cacheType === CacheType.Session) {
            sessionStorage.setItem(cacheItem.key, JSON.stringify(cacheItem));
        } else {
            localStorage.setItem(cacheItem.key, JSON.stringify(cacheItem));
        }
    }

    private isExpired(cacheItem: CacheItem): boolean {
        if (cacheItem.expiresTTL === -1) {
            return false; // Never expires
        }
        const now = Date.now();
        const expirationTime = cacheItem.insertedAt + (cacheItem.expiresTTL * 1000);
        return now > expirationTime;
    }

    get<T>(key: string, defaultValue: T | null = null): T | null {
        var sessionItem = sessionStorage.getItem(key);
        if (sessionItem) {
            const cacheItem: CacheItem = JSON.parse(sessionItem);
            if (this.isExpired(cacheItem)) {
                sessionStorage.removeItem(key);
                return defaultValue;
            }
            return cacheItem.value as T;
        }

        var localItem = localStorage.getItem(key);
        if (localItem) {
            const cacheItem: CacheItem = JSON.parse(localItem);
            if (this.isExpired(cacheItem)) {
                localStorage.removeItem(key);
                return defaultValue;
            }

            return cacheItem.value as T;
        }

        return defaultValue;
    }

    /**
     * Sets the HTTP response in cache.
     *
     * @param {string} key - The key to associate with the data in the cache.
     * @param resp - The HTTP response to be stored in the cache.
     * @param {CacheType} [cacheType=CacheType.Session] - The type of cache to use (Session or Local).
     * @param {number} [60000] - The time-to-live (TTL) for the cached data in milliseconds.
     */
    setHttpResponse(key: string, resp: HttpResponse<any>, cacheType: CacheType = CacheType.Session, ttl: number = -1): void {
        const cacheItem = new CacheItem(key, resp.clone({ body: resp.body }), cacheType, ttl);
        this.set(cacheItem);
    }

    del(key) {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
    }

    /**
     * Clears the cache entries that match the specified cache key. This can match on a partial cache key
     * @param cacheKey - The cache key to match against.
     */
    cacheBust(cacheKey: string) {
        const testKey = cacheKey.toLocaleLowerCase();
        for (let index = 0; index < localStorage.length; index++) {
            const key = localStorage.key(index);
            if (key && key.toLocaleLowerCase().indexOf(testKey) > -1) {
                localStorage.removeItem(key);
            }
        }

        for (let index = 0; index < sessionStorage.length; index++) {
            const key = sessionStorage.key(index);
            if (key && key.toLocaleLowerCase().indexOf(testKey) > -1) {
                sessionStorage.removeItem(key);
            }
        }
    }
}
